import * as React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ReportContext } from '../../store/reportContext';

type Props = {
	storage: StorageType;
};

const StockedItemsInventoryReports: React.FC<Props> = ({ storage }) => {
	const theme = useTheme();
	const { getReports, getReportsLoading } = useContext(ReportContext);
	const [reports, setReports] = useState<VptReport[]>([]);

	useEffect(() => {
		getReports(false, { storageId: storage.id }, (results) => {
			setReports(results.reportHeaders || []);
		});
	}, [getReports, storage.id]);

	const yearlyData = useMemo(() => {
		const map = new Map<number, { stockedItems: number; inventoryReports: number }>();

		reports.forEach((report) => {
			const year = new Date(report.date).getFullYear();
			const current = map.get(year) || { stockedItems: 0, inventoryReports: 0 };
			const stockedItems = Array.isArray(report.stockingLogs)
				? report.stockingLogs.reduce((sum, log) => sum + (log.amountAdded || 0), 0)
				: 0;

			map.set(year, {
				stockedItems: current.stockedItems + stockedItems,
				inventoryReports: current.inventoryReports + (report.inventory ? 1 : 0),
			});
		});

		return Array.from(map.entries())
			.map(([year, values]) => ({ year, ...values }))
			.sort((a, b) => b.year - a.year);
	}, [reports]);

	const currentYear = new Date().getFullYear();
	const formatYearLabel = (year: number) => (year === currentYear ? `${year} (YTD)` : `${year}`);

	return (
		<Grid container spacing={2} size={{ xs: 6 }} sx={{ p: 2 }}>
			<Grid size={{ xs: 6 }}>
				<Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
					Stocked items
				</Typography>
				{getReportsLoading && <Box>Loading...</Box>}
				{!getReportsLoading && yearlyData.length === 0 && <Box>-</Box>}
				{!getReportsLoading && yearlyData.map((row) => (
					<Box key={`stocked-${row.year}`}>{`${formatYearLabel(row.year)} ${row.stockedItems}`}</Box>
				))}
			</Grid>
			<Grid size={{ xs: 6 }}>
				<Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
					Inventory reports
				</Typography>
				{getReportsLoading && <Box>Loading...</Box>}
				{!getReportsLoading && yearlyData.length === 0 && <Box>-</Box>}
				{!getReportsLoading && yearlyData.map((row) => (
					<Box key={`inventory-${row.year}`}>{`${formatYearLabel(row.year)} ${row.inventoryReports}`}</Box>
				))}
			</Grid>
		</Grid>
	);
};

export default StockedItemsInventoryReports;
